import { customerDraft } from 'client/actions/customer'
import { updateCustomSettings } from 'client/actions/customSettings'
import { getDocumentPreview } from 'client/actions/draft'
import { getExperiments } from 'client/actions/experiments'
import { defaultDocument } from 'client/actions/helpers/draft'
import { navigateAddModal, navigateRemoveModal } from 'client/actions/navigation'
import { updatePayNowStatus } from 'client/actions/payNowManagement'
import LoadingEllipsis from 'client/components/common/LoadingEllipsis'
import { InvoiceTemplateForm } from 'client/components/Modal/CompanyInformationModal/InvoiceTemplateForm'
import { PremiumFeatureType } from 'client/components/Modal/PremiumFeatureIntermediaryModal'
import { cleanPaymentDetails } from 'client/components/MyAccount/views/PaymentInformation'
import { Analytics, checkPlacement } from 'client/Global/Analytics'
import { AnalyticsType, Category, Events, PremiumModalOrigin, Result } from 'client/Global/AnalyticsEvents'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { LogoURLInterface } from 'client/Interfaces/CompanyInterface'
import { CustomSettingsInterface } from 'client/Interfaces/CustomSettingsInterface'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { LineInterface } from 'client/Interfaces/Draft/LineInterface'
import { ExternalTaxRatesInterface } from 'client/Interfaces/ExternalVATRatesInterface'
import { PayNowInterface } from 'client/Interfaces/PayNow/AccountAuthorizationParams'
import { SettingsInterface } from 'client/Interfaces/Settings/SettingsInterface'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { QuotaTypes, SubscriptionInterface, SubscriptionStatus } from 'client/Interfaces/Subscription'
import { getInvoiceTypes } from 'client/Other/Configs'
import { isValidEmailAddress } from 'client/Other/functions/validationFunctions'
import { Immutable } from 'client/Other/Immutable'
import { deFlattenObject } from 'client/Other/Object'
import { l } from 'client/Other/Translation'
import { ExperimentsState } from 'client/reducers/experiments'
import { doesCurrentPlanSupportFeature } from 'client/reducers/Subscription/companySubscription'
import * as React from 'react'
import { connect } from 'react-redux'

import { getExternalVATRates } from '../../../actions/agerasExternal'
import * as CompanyActions from '../../../actions/company'
import * as SettingsActions from '../../../actions/settings'
import Button, { ButtonType } from '../../common/Button/Button'
import { Modal } from '../../common/Modal/Modal'
import Tabs, { Tab } from '../../common/Tabs/Tabs'
import CompanyInformationForm from './CompanyInformationForm'
import './CompanyInformationModal.scss'
import DynamicPreview from './DynamicPreview/DynamicPreview'
import PaymentDetailsForm from './PaymentDetailsForm'

export enum TABS {
  companyDetails = 'companyDetails',
  paymentDetails = 'paymentDetails',
  invoiceTemplate = 'invoiceTemplate',
}

interface PropsType {
  activeTab: string
  tradeNameId?: number
  originUrl?: string
  originDocumentType?: DocumentTypes
  companySchema: any
  paymentMethodsSchema: any
  companySettingsValues: any
  companyValues: any
  paymentMethodsValues: any
  vatOptions: any
  locale: string
  withoutPreview?: boolean
  payNow: PayNowInterface
  experiments: ExperimentsState
  stateSettings: SettingsInterface
  subscription: SubscriptionInterface
  origin?: string
  externalVATRates: ExternalTaxRatesInterface
  isAgerasNextMigration?: boolean
  next: () => void
  onSave?: () => void
  onCancel?: () => void
  getExternalVATRates: () => void
}

interface IActionProps {
  openVATModal: () => void
  closeModal: (activeTab: string) => void
  saveData: (
    data,
    companyChangedFields,
    paymentValuesChangedFields,
    customSettingsChangedFields,
    next,
    activeTab,
    subscriptionStatus: SubscriptionStatus,
  ) => void
  loadPreview: (settings, tradeNameId, originDocumentType) => void
  onBeforeLogoUpload?: () => Promise<void>
  onAfterLogoUpload?: () => Promise<void>
  openActivatePayNowModal: (originUrl: string) => void
  togglePayNowStatus: (status: boolean) => void
  saveSettings: (settings: CustomSettingsInterface) => void
  getExperiments: () => Promise<void>
  openSubscriptionModal: (customSettings: CustomSettingsInterface) => void
  openLogoModal: (
    logo: string,
    fileName: string,
    changeLogoField: (logoId: string, content: string) => void,
    uploadLogo: (image: Blob, fileName: string) => Promise<LogoURLInterface>,
  ) => void
}

interface StateType {
  companyValues?: any
  paymentValues?: any
  activeTab?: string
  companyChangedFields?: string[]
  paymentValuesChangedFields?: string[]
  autoFilledFields?: any
  customSettings?: CustomSettingsInterface
  customSettingsChangedFields?: string[]
  loaded: boolean
  canUseCustomSettings: boolean
}

const PREVIEW_LOAD_DELAY = 600
let ibanValidationResult = true
let swiftValidationResult = true
class CompanyInformationModalClass extends React.Component<PropsType & IActionProps, StateType> {
  private timer
  private paymentTabRef: React.RefObject<HTMLDivElement>
  constructor(props) {
    super(props)
    this.state = {
      loaded: false,
      canUseCustomSettings: doesCurrentPlanSupportFeature(this.props.subscription, QuotaTypes.CUSTOM_INVOICE_STYLING),
    }
    this.timer = null
    this.paymentTabRef = React.createRef()
  }

  public async componentDidMount() {
    if (!this.props.withoutPreview) {
      this.props.loadPreview({}, this.props.tradeNameId, this.props.originDocumentType)
    }
    if (!this.props.experiments || this.props.experiments?.loaded === false) {
      await this.props.getExperiments()
    }
    if (this.props.experiments?.loaded === true) {
      this.setState({ loaded: true })
    }
    if (this.props.isAgerasNextMigration) {
      this.props.getExternalVATRates()
    }
  }

  private onCompanyValuesChange = (newValues, changedFields, autoFilledFields = null) => {
    this.setState(
      {
        companyValues: newValues,
        companyChangedFields: changedFields,
        autoFilledFields,
      },
      () => {
        this.updatePreview()
      },
    )
  }

  private onPaymentValuesChange = (newValues, changedFields) => {
    this.setState(
      {
        paymentValues: newValues,
        paymentValuesChangedFields: changedFields,
      },
      () => this.updatePreview(),
    )
  }

  private onCustomSettingsChange = (newSettings, changedFields) => {
    this.setState(
      {
        customSettings: newSettings,
        customSettingsChangedFields: changedFields,
      },
      () => this.updatePreview(),
    )
  }

  private getCompanyData = () => {
    const { name, email, businessId, vatNumber, country, vatPct } = this.props.companyValues
    return {
      'company.name': name,
      'company.email': email,
      'company.businessId': businessId,
      'company.vatNumber': vatNumber,
      'company.country': country,
      'company.vat': vatPct,
    }
  }

  private getFirstTradeName() {
    return this.props.companySettingsValues.tradeNames[0]
  }

  private isValid(): boolean {
    const emailAcceptable = (m) => typeof m !== 'string' || m === '' || isValidEmailAddress(m)
    const { companyValues } = this.state
    if (!companyValues) {
      return true
    }
    const contactEmail = companyValues['contact.email']
    const companyEmail = companyValues['company.email']
    return emailAcceptable(contactEmail) && emailAcceptable(companyEmail)
  }

  private constructSettingsValues() {
    if (!this.state.companyValues) {
      return
    }
    const companySettings = deFlattenObject(this.state.companyValues)
    const tradeNames = this.props.companySettingsValues.tradeNames.map((tn) => {
      let tradeName = tn
      if (tn._id === this.props.tradeNameId) {
        const { address, contact, logo, name, _id } = companySettings
        const logoObj = logo && logo.id ? { logo: { id: logo.id, content: logo.content } } : {}
        tradeName = { ...logoObj, address, contact, default: companySettings.default, name, _id }
      }
      return tradeName
    })
    const { name, email, vatNumber, businessId, ...company } = companySettings.company
    const newCompanySettings = { ...company, tradeNames }
    delete companySettings.company
    return newCompanySettings
  }

  private constructCompanyValues() {
    if (!this.state.companyValues) {
      return
    }
    const { name, email, vatNumber, businessId, vat } = deFlattenObject(this.state.companyValues).company
    return { name, email, vatNumber, businessId, vatPct: vat }
  }

  private constructPaymentDetailsValues() {
    if (!this.state.paymentValues) {
      return
    }
    return this.state.paymentValues
  }

  private constructCustomSettingsValues() {
    if (!this.state.customSettings) {
      return
    }
    return this.state.customSettings
  }

  private onClose = () => {
    if (this.props.onCancel) {
      this.props.onCancel()
    }
    this.props.closeModal(this.state.activeTab || this.props.activeTab)
  }

  private updatePreview = () => {
    if (!this.props.withoutPreview) {
      const companySettingsData = this.constructSettingsValues()
      const companyValues = this.constructCompanyValues()
      const paymentDetailsValues = this.constructPaymentDetailsValues()
      const customSettingsValues = this.constructCustomSettingsValues()
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.props.loadPreview(
          {
            companySettingsData,
            companyValues,
            paymentDetailsValues,
            customSettingsValues,
          },
          this.props.tradeNameId,
          this.props.originDocumentType,
        )
      }, PREVIEW_LOAD_DELAY)
    }
  }

  private getIbans(validationResult) {
    ibanValidationResult = validationResult
  }

  private getSwifts(validationResult) {
    swiftValidationResult = validationResult
  }

  private onSave = async () => {
    if (this.isValid && ibanValidationResult && swiftValidationResult) {
      const companySettingsData = this.constructSettingsValues()
      const companyValues = this.constructCompanyValues()
      const paymentDetailsValues = this.constructPaymentDetailsValues()
      let customSettingsValues
      if (this.state.canUseCustomSettings) {
        customSettingsValues = this.constructCustomSettingsValues()
      }
      const { autoFilledFields } = this.state
      await this.props.saveData(
        {
          companySettingsData,
          companyValues,
          paymentDetailsValues,
          customSettingsValues,
          autoFilledFields,
        },
        this.state.companyChangedFields,
        this.state.paymentValuesChangedFields,
        this.state.customSettingsChangedFields,
        this.props.next,
        this.state.activeTab || this.props.activeTab,
        this.props.subscription.status,
      )

      if (this.props.onSave) {
        this.props.onSave()
      }
    } else if (!ibanValidationResult || !swiftValidationResult) {
      this.paymentTabRef.current.click()
    }
  }

  public render() {
    return (
      <Modal
        fullscreen={true}
        className="companyInformationModal"
        size="medium"
        isOpen={true}
        onClose={() => this.onClose()}
      >
        {this.state.loaded ? (
          <div className="companyInformationModal__form">
            <span className={'companyInformationModal__header'}>{l('companyInformationModal.header')}</span>
            <Tabs
              paymentTabRef={this.paymentTabRef}
              automation="companyDetails"
              defaultActive={this.props.activeTab}
              onChange={(tab) => {
                this.setState({
                  activeTab: tab,
                })
                Analytics.trackEvent(Events.editSettings, {
                  type: AnalyticsType.modal,
                  category: Category.setting,
                  result: Result.click,
                  placement: checkPlacement(window.location.pathname),
                  clickedTab: tab,
                })
              }}
            >
              <Tab key={TABS.companyDetails} title={l('companyInformationModal.title.companyDetails')}>
                <CompanyInformationForm
                  schema={this.props.companySchema}
                  values={this.props.companySettingsValues}
                  company={this.getCompanyData()}
                  openVATModal={() => this.props.openVATModal()}
                  vatOptions={this.props.vatOptions}
                  locale={this.props.locale}
                  tradeNameId={this.props.tradeNameId || this.getFirstTradeName()._id}
                  onChange={(vals, changedFields, autoFilledFields) => {
                    return this.onCompanyValuesChange(vals, changedFields, autoFilledFields)
                  }}
                  beforeLogoUpload={this.props.onBeforeLogoUpload}
                  afterLogoUpload={this.props.onAfterLogoUpload}
                  openLogoModal={this.props.openLogoModal}
                  externalVATRates={this.props.externalVATRates}
                  isAgerasNextMigration={this.props.isAgerasNextMigration}
                />
              </Tab>
              <Tab key={TABS.paymentDetails} title={l('companyInformationModal.title.paymentDetails')}>
                <PaymentDetailsForm
                  locale={this.props.locale}
                  schema={this.props.paymentMethodsSchema}
                  values={this.props.paymentMethodsValues}
                  onChange={(vals, changedFields) => this.onPaymentValuesChange(vals, changedFields)}
                  onOpenActivatePayNowModal={() => {
                    this.props.openActivatePayNowModal(this.props.originUrl)
                  }}
                  payNow={this.props.payNow}
                  getIbans={this.getIbans}
                  getSwifts={this.getSwifts}
                  stateSettings={this.props.stateSettings}
                  togglePayNowStatus={this.props.togglePayNowStatus}
                />
              </Tab>
              <Tab key={TABS.invoiceTemplate} title={l('companyInformationModal.title.customTemplates')}>
                <InvoiceTemplateForm
                  activeTab={this.state.activeTab || this.props.activeTab}
                  onChange={(val, changedVal) => this.onCustomSettingsChange(val, changedVal)}
                  saveSettings={(settings) => this.props.saveSettings(settings)}
                  canUseCustomSettings={this.state.canUseCustomSettings}
                  openSubscriptionModal={(customSettings) => this.props.openSubscriptionModal(customSettings)}
                />
              </Tab>
            </Tabs>
          </div>
        ) : (
          <LoadingEllipsis />
        )}
        {!this.props.withoutPreview && (
          <div className="companyInformationModal__preview">
            <DynamicPreview />
          </div>
        )}
        <footer>
          <div className={'buttongroup'}>
            <Button
              automation={'companyInformationModal.save'}
              label={l('Save')}
              type={ButtonType.accept}
              action={this.onSave}
            />
            <Button label={l('Cancel')} type={ButtonType.cancel} action={this.onClose} />
          </div>
        </footer>
      </Modal>
    )
  }
}

const mapStateToProps = (state: StateInterface) => {
  return {
    companySchema: state.settings.schemas.company,
    paymentMethodsSchema: state.settings?.schemas?.paymentMethods,
    companySettingsValues: state.settings?.values?.company,
    companyValues: state.company,
    paymentMethodsValues: state.settings?.values?.paymentMethods,
    vatOptions: state.settings?.managedFields?.VAT,
    locale: state.token?.locale,
    payNow: state.payNow,
    subscription: state.subscriptionManagement?.companySubscription?.subscription,
    experiments: state.experiments,
    stateSettings: state.settings,
    externalVATRates: state.externalVATRates,
    isAgerasNextMigration: state.company.agerasNextMigration,
  }
}

const mapDispatchToProps = (dispatch): IActionProps => ({
  openLogoModal: (logo, fileName, changeLogoField, uploadLogo) => {
    dispatch((dispatch, getState) => {
      const { settings } = getState()
      const country = settings.values.company.country
      dispatch(
        navigateAddModal({
          component: 'CropLogoModal',
          props: {
            isOpen: true,
            logo,
            fileName,
            country,
            close: () => {
              dispatch(navigateRemoveModal('CropLogoModal'))
            },
            changeLogoField,
            uploadLogo,
          },
        }),
      )
    })
  },
  onBeforeLogoUpload: async () => {
    return dispatch((dispatch, getState) => {
      const { settings } = getState()
      return dispatch(SettingsActions.updateSettings(settings.values))
    })
  },

  onAfterLogoUpload: async () => {
    return dispatch((dispatch, getState) => {
      const { settings } = getState()
      return dispatch(SettingsActions.getSettings())
    })
  },

  saveData: async (
    { companySettingsData, companyValues, paymentDetailsValues, customSettingsValues, autoFilledFields }: any,
    companyChangedFields: string[] = [],
    paymentValuesChangedFields: string[] = [],
    customSettingsChangedFields: string[] = [],
    next: () => void,
    activeTab = '',
    subscriptionStatus,
  ) => {
    const paymentMethods = cleanPaymentDetails(paymentDetailsValues)
    const settings = {
      ...(companySettingsData ? { company: companySettingsData } : {}),
      ...(paymentMethods ? { paymentMethods } : {}),
    }
    if (companyChangedFields.includes('company.isNotVATLiable')) {
      await dispatch(SettingsActions.updateInvoiceSettingsOnVATLiableChange(settings.company.isNotVATLiable))
    }
    await dispatch(SettingsActions.updateSettings(settings))
    if (companyValues) {
      await dispatch(
        CompanyActions.updateCompany(
          companyValues,
          settings.company.isNotVATLiable,
          subscriptionStatus !== 'no_subscription' &&
            (companyChangedFields.includes('company.isNotVATLiable') ||
              companyChangedFields.includes('company.vatNumber')),
        ),
      )
    }
    if (customSettingsValues) {
      await dispatch(updateCustomSettings(customSettingsValues))
    }
    Analytics.trackEvent(Events.editSettings, {
      type: AnalyticsType.modal,
      category: Category.setting,
      result: Result.success,
      placement: checkPlacement(window.location.pathname),
      tab: activeTab,
      changedFields: companyChangedFields.concat(paymentValuesChangedFields).concat(customSettingsChangedFields),
      autoFilledFields,
    })
    dispatch(navigateRemoveModal('CompanyInformationModal'))
    if (next) {
      next()
    }
  },
  closeModal: (activeTab: string) => {
    Analytics.trackEvent(Events.editSettings, {
      type: AnalyticsType.modal,
      category: Category.setting,
      result: Result.cancel,
      placement: checkPlacement(window.location.pathname),
      tab: activeTab,
    })
    dispatch(navigateRemoveModal('CompanyInformationModal'))
  },
  openVATModal: () =>
    dispatch(
      navigateAddModal({
        component: 'VATModal',
        props: {
          isOpen: true,
        },
      }),
    ),
  loadPreview: (
    { companySettingsData, paymentDetailsValues, companyValues, customSettingsValues },
    tradeNameId,
    originDocumentType,
  ) => {
    dispatch((dispatch, getState) => {
      // construct document out of settings being edited and fetch preview for that
      const { settings, company, token, customers, config } = getState()
      const newSettings = {
        ...settings,
        values: {
          ...settings.values,
          ...(companySettingsData ? { company: companySettingsData } : {}),
          ...(paymentDetailsValues ? { paymentMethods: paymentDetailsValues } : {}),
        },
      }
      const newCompany = {
        ...company,
        ...companyValues,
      }
      const documentType =
        originDocumentType === DocumentTypes.ESTIMATE ? DocumentTypes.ESTIMATE : DocumentTypes.INVOICE
      const document = defaultDocument(documentType, newSettings, token, newCompany, getState(), customSettingsValues)
      if (tradeNameId) {
        document.invoicer.tradeName = newSettings.values.company.tradeNames.find((t) => t._id === tradeNameId)
      }
      if (document.invoicer.tradeName) {
        document.invoicer.tradeName.country = company.country
      }
      const firstLine = document.lines[0] as LineInterface

      const invoiceTypes = getInvoiceTypes(config, company.country, documentType)
      const defaultInvoiceType = invoiceTypes.find(
        ({ type }) => type === (newSettings.values.company.isNotVATLiable ? 'SALES_VATNOLIABLE' : 'SALES'),
      )
      document.fields.columns.value = defaultInvoiceType.fields
      if (firstLine && !firstLine.description) {
        firstLine.description = l('exampleProduct')
      }
      try {
        const name = document.invoicer.tradeName.contact.name
        name.full = `${name.first} ${name.last}`
      } catch (e) {
        console.error('Name not found')
      }
      delete document.isDirty
      delete document.invalidFields
      delete document.fields.customer
      delete document.fields.sender
      delete document.fields.exchangeRate
      document.invoiceNumber = {
        prefix: String(new Date().getFullYear()),
        number: 12345,
      }
      document.fields.invoiceNumber.value = `${document.invoiceNumber.prefix} - ${document.invoiceNumber.number}`
      if (customers && customers.collection && customers.collection.length > 0) {
        document.receiver = customers.collection.sort((c1, c2) => c1.id - c2.id)[0]
      } else {
        document.receiver = Immutable.copy(customerDraft)
        document.receiver.id = 0
      }
      dispatch(getDocumentPreview(document))
    })
  },
  openActivatePayNowModal: (originUrl) => {
    Analytics.trackEvent(Events.olpSignupModal, {
      type: AnalyticsType.modal,
      category: Category.onlinePayment,
      result: Result.open,
      placement: checkPlacement(window.location.pathname),
    })
    dispatch(
      navigateAddModal({
        component: 'ActivatePayNowModal',
        props: {
          isOpen: true,
          originUrl,
        },
      }),
    )
  },
  togglePayNowStatus: (status: boolean) => dispatch(updatePayNowStatus({ active: status })),
  saveSettings: (settings) => {
    dispatch(updateCustomSettings(settings))
  },
  getExperiments: () => dispatch(getExperiments()),
  openSubscriptionModal: (settings) => {
    const openSubscriptionModal = () =>
      dispatch(
        navigateAddModal({
          component: 'PremiumPlanModal',
          props: {
            origin: PremiumModalOrigin.DocumentCustomisation,
            hideDowngradePlans: false,
            next: () => {
              dispatch(updateCustomSettings(settings))
              return dispatch(
                navigateAddModal({
                  component: 'CompanyInformationModal',
                  props: {
                    activeTab: TABS.invoiceTemplate,
                  },
                }),
              )
            },
          },
        }),
      )
    const openIntermediary = () => {
      dispatch(
        navigateAddModal({
          component: 'PremiumFeatureIntermediaryModal',
          props: {
            isOpen: true,
            featureType: PremiumFeatureType.DOCUMENT_CUSTOMISATION,
            next: openSubscriptionModal,
          },
        }),
      )
    }
    openIntermediary()
  },
  getExternalVATRates: () => dispatch(getExternalVATRates()),
})

export default connect(mapStateToProps, mapDispatchToProps)(CompanyInformationModalClass)
